<template>
  <div class="terms-page">
    <!-- Header -->
    <section class="page-header">
      <div class="container">
        <h1>{{ t('terms.title') }}</h1>
        <p class="page-subtitle">{{ t('terms.lastUpdated') }}</p>
      </div>
    </section>

    <!-- Content -->
    <section class="policy-content">
      <div class="container">
        <!-- Acceptance -->
        <div class="policy-section">
          <h2>{{ t('terms.acceptance') }}</h2>
          <p>{{ t('terms.acceptanceText') }}</p>
        </div>

        <!-- User Agreement -->
        <div class="policy-section">
          <h2>{{ t('terms.userAgreement') }}</h2>
          <p>{{ t('terms.userAgreementText') }}</p>
          <h3>{{ t('terms.requirements') }}</h3>
          <ul>
            <li>{{ t('terms.requirement1') }}</li>
            <li>{{ t('terms.requirement2') }}</li>
            <li>{{ t('terms.requirement3') }}</li>
            <li>{{ t('terms.requirement4') }}</li>
          </ul>
        </div>

        <!-- Use Rights -->
        <div class="policy-section">
          <h2>{{ t('terms.useRights') }}</h2>
          <p>{{ t('terms.useRightsText') }}</p>
        </div>

        <!-- Restrictions -->
        <div class="policy-section">
          <h2>{{ t('terms.restrictions') }}</h2>
          <p>{{ t('terms.restrictionsText') }}</p>
          <h3>{{ t('terms.prohibited') }}</h3>
          <ul>
            <li>{{ t('terms.prohibited1') }}</li>
            <li>{{ t('terms.prohibited2') }}</li>
            <li>{{ t('terms.prohibited3') }}</li>
            <li>{{ t('terms.prohibited4') }}</li>
          </ul>
        </div>

        <!-- Intellectual Property -->
        <div class="policy-section">
          <h2>{{ t('terms.intellectualProperty') }}</h2>
          <p>{{ t('terms.intellectualPropertyText') }}</p>
        </div>

        <!-- Disclaimers -->
        <div class="policy-section">
          <h2>{{ t('terms.disclaimers') }}</h2>
          <p>{{ t('terms.disclaimersText') }}</p>
        </div>

        <!-- Limitation -->
        <div class="policy-section">
          <h2>{{ t('terms.limitation') }}</h2>
          <p>{{ t('terms.limitationText') }}</p>
        </div>

        <!-- Termination -->
        <div class="policy-section">
          <h2>{{ t('terms.termination') }}</h2>
          <p>{{ t('terms.terminationText') }}</p>
        </div>

        <!-- Modifications -->
        <div class="policy-section">
          <h2>{{ t('terms.modifications') }}</h2>
          <p>{{ t('terms.modificationsText') }}</p>
        </div>

        <!-- Governing Law -->
        <div class="policy-section">
          <h2>{{ t('terms.governingLaw') }}</h2>
          <p>{{ t('terms.governingLawText') }}</p>
        </div>

        <!-- Contact -->
        <div class="policy-section contact-section">
          <h2>{{ t('terms.contactUs') }}</h2>
          <p>{{ t('terms.contactUsText') }}</p>
          <p><strong>Email:</strong> <a href="mailto:contact@upgame.top">contact@upgame.top</a></p>
          <p><strong>Website:</strong> <a href="https://upgame.top" target="_blank">upgame.top</a></p>
        </div>

        <!-- Back Button -->
        <button class="btn-back" @click="goHome">{{ t('buttons.backHome') }}</button>
      </div>
    </section>
  </div>
</template>

<script setup>
import { useI18n } from '@/utils/i18n'

const { t } = useI18n()

const goHome = () => {
  window.location.hash = '#/'
}
</script>

<style scoped>
.terms-page {
  min-height: 100vh;
  background: var(--color-bg-light);
}

/* Header */
.page-header {
  background: var(--gradient-primary);
  color: white;
  padding: var(--space-5xl) var(--space-base);
  text-align: center;
}

.page-header h1 {
  font-size: clamp(28px, 6vw, 48px);
  margin: 0 0 var(--space-base) 0;
  font-weight: var(--font-weight-bold);
}

.page-subtitle {
  font-size: var(--font-size-body);
  margin: 0;
  opacity: 0.95;
}

/* Content */
.container {
  max-width: 900px;
  margin: 0 auto;
  padding: var(--space-4xl) var(--space-base);
}

.policy-content {
  background: var(--color-bg-light);
}

/* Policy Sections */
.policy-section {
  margin-bottom: var(--space-3xl);
  padding-bottom: var(--space-3xl);
  border-bottom: 1px solid var(--color-border);
}

.policy-section:last-of-type {
  border-bottom: none;
}

.policy-section h2 {
  font-size: var(--font-size-h4);
  color: var(--color-text-primary);
  margin: 0 0 var(--space-lg) 0;
  font-weight: var(--font-weight-bold);
}

.policy-section h3 {
  font-size: var(--font-size-h5);
  color: var(--color-text-primary);
  margin: var(--space-lg) 0 var(--space-md) 0;
  font-weight: var(--font-weight-bold);
}

.policy-section p {
  font-size: var(--font-size-body);
  color: var(--color-text-secondary);
  line-height: 1.8;
  margin: 0 0 var(--space-md) 0;
}

.policy-section ul {
  margin: var(--space-md) 0;
  padding-left: var(--space-2xl);
  list-style: disc;
}

.policy-section li {
  font-size: var(--font-size-body);
  color: var(--color-text-secondary);
  line-height: 1.8;
  margin-bottom: var(--space-sm);
}

.policy-section a {
  color: var(--color-primary-orange);
  text-decoration: none;
  font-weight: var(--font-weight-semibold);
  transition: color var(--transition-base) var(--ease-in-out));
}

.policy-section a:hover {
  color: var(--color-primary-gold);
  text-decoration: underline;
}

/* Contact Section */
.contact-section {
  background: var(--color-bg-card);
  padding: var(--space-2xl);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  margin-bottom: var(--space-3xl);
  color: var(--color-text-primary);
}

/* Back Button */
.btn-back {
  display: block;
  margin: var(--space-3xl) auto 0;
  padding: var(--space-md) var(--space-2xl);
  background: var(--color-bg-card);
  color: var(--color-text-primary);
  border: 2px solid var(--color-border);
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out));
  font-family: var(--font-heading);
  font-size: var(--font-size-body);
}

.btn-back:hover {
  border-color: var(--color-primary-orange);
  color: var(--color-primary-orange);
  background: rgba(255, 107, 53, 0.05);
}

/* Responsive */
@media (max-width: 640px) {
  .page-header {
    padding: var(--space-3xl) var(--space-base);
  }

  .container {
    padding: var(--space-2xl) var(--space-base);
  }

  .policy-section {
    margin-bottom: var(--space-2xl);
    padding-bottom: var(--space-2xl);
  }

  .policy-section h2 {
    font-size: var(--font-size-h5);
  }

  .policy-section h3 {
    font-size: var(--font-size-body);
  }

  .policy-section p,
  .policy-section li {
    font-size: var(--font-size-small);
  }

  .btn-back {
    width: 100%;
  }
}
</style>
